import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WeddingChecklistSection = () => {
  const [checklistItems, setChecklistItems] = useState([
    { id: 1, task: 'Book wedding venue', completed: false, priority: 'high', category: 'Venue' },
    { id: 2, task: 'Choose catering service', completed: false, priority: 'high', category: 'Catering' },
    { id: 3, task: 'Hire wedding photographer', completed: false, priority: 'high', category: 'Photography' },
    { id: 4, task: 'Book mehendi artist', completed: false, priority: 'medium', category: 'Beauty' },
    { id: 5, task: 'Order wedding invitations', completed: false, priority: 'medium', category: 'Invitations' },
    { id: 6, task: 'Select wedding music/DJ', completed: false, priority: 'medium', category: 'Entertainment' },
    { id: 7, task: 'Book transportation', completed: false, priority: 'low', category: 'Transportation' },
    { id: 8, task: 'Choose wedding favors', completed: false, priority: 'low', category: 'Favors' },
    { id: 9, task: 'Book honeymoon accommodation', completed: false, priority: 'medium', category: 'Honeymoon' },
    { id: 10, task: 'Purchase wedding rings', completed: false, priority: 'high', category: 'Jewelry' }
  ]);

  const [newTask, setNewTask] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const handleToggleTask = (taskId) => {
    setChecklistItems(prev => 
      prev.map(item => 
        item.id === taskId 
          ? { ...item, completed: !item.completed }
          : item
      )
    );
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const newItem = {
        id: Date.now(),
        task: newTask.trim(),
        completed: false,
        priority: 'medium',
        category: 'Other'
      };
      setChecklistItems(prev => [...prev, newItem]);
      setNewTask('');
    }
  };

  const handleDeleteTask = (taskId) => {
    setChecklistItems(prev => prev.filter(item => item.id !== taskId));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getFilteredItems = () => {
    switch (activeFilter) {
      case 'completed': return checklistItems.filter(item => item.completed);
      case 'pending': return checklistItems.filter(item => !item.completed);
      case 'high': return checklistItems.filter(item => item.priority === 'high');
      default: return checklistItems;
    }
  };

  const completedCount = checklistItems.filter(item => item.completed).length;
  const completionPercentage = Math.round((completedCount / checklistItems.length) * 100);

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading font-semibold text-2xl text-text-primary">
              Wedding Checklist
            </h2>
            <p className="text-text-secondary">
              {completedCount} of {checklistItems.length} tasks completed
            </p>
          </div>
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary-600 flex items-center justify-center mb-2">
              <span className="text-white font-bold text-lg">{completionPercentage}%</span>
            </div>
            <p className="text-xs text-text-secondary">Complete</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-primary to-primary-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Add New Task */}
        <div className="flex space-x-2">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a new planning task..."
            className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
            onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
          />
          <Button
            variant="primary"
            iconName="Plus"
            onClick={handleAddTask}
            disabled={!newTask.trim()}
          >
            Add Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: 'all', label: 'All Tasks' },
            { key: 'pending', label: 'Pending' },
            { key: 'completed', label: 'Completed' },
            { key: 'high', label: 'High Priority' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeFilter === filter.key
                  ? 'bg-primary text-white shadow-soft'
                  : 'bg-surface-100 text-text-secondary hover:bg-surface-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Checklist Items */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {getFilteredItems().map((item) => (
            <div 
              key={item.id}
              className={`flex items-center space-x-4 p-4 rounded-lg border transition-all duration-200 ${
                item.completed 
                  ? 'bg-green-50 border-green-200' :'bg-surface-50 border-border hover:bg-surface-100'
              }`}
            >
              <button
                onClick={() => handleToggleTask(item.id)}
                className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                  item.completed
                    ? 'bg-green-500 border-green-500 text-white' :'border-gray-300 hover:border-primary'
                }`}
              >
                {item.completed && <Icon name="Check" size={16} />}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-1">
                  <span className={`text-base font-medium ${
                    item.completed ? 'line-through text-gray-500' : 'text-text-primary'
                  }`}>
                    {item.task}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(item.priority)}`}>
                    {item.priority}
                  </span>
                </div>
                <p className="text-sm text-text-muted">{item.category}</p>
              </div>

              {/* Find Vendors Button - Only show for incomplete tasks */}
              {!item.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Search"
                  onClick={() => {/* Navigate to vendor search */}}
                >
                  Find Vendors
                </Button>
              )}

              <button
                onClick={() => handleDeleteTask(item.id)}
                className="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
              >
                <Icon name="Trash2" size={18} />
              </button>
            </div>
          ))}
        </div>

        {getFilteredItems().length === 0 && (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No tasks found for this filter</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeddingChecklistSection;